<template>
  <Modal
    v-model:visible="isVisible"
    class="email-to-carrier-modal"
    :header="t('shipments.resendConfirmation')"
    :dismissable-mask="!isAwaitingEmailSent"
    @keypress.esc="onCancel"
  >
    <InputWrapper
      v-slot="{ ariaProps, inputClasses, inputId, invalid }"
      input-id="email-resend-confirmation-modal-emails"
      :label="t('shipments.emailsCommaSeparated')"
      :errors="v$.email.$errors"
    >
      <InputText
        :id="inputId"
        v-model="internalEmails.email"
        v-bind="ariaProps"
        :class="inputClasses"
        :invalid="invalid"
        :disabled="emailDisabled"
        :placeholder="t('shipments.emailsCommaSeparated')"
      />
    </InputWrapper>

    <template #footer>
      <RygenButton
        mode="tertiary"
        :label="t('common.cancel')"
        @click="isVisible = false"
      />

      <RygenButton
        :label="t('common.confirm')"
        icon="pi pi-check"
        :loading="isAwaitingEmailSent"
        @click="onConfirm"
      />
    </template>
  </Modal>
</template>

<script lang="ts" setup>
import { computed, PropType, ref, toRefs, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import {
  matchesEmail,
  required,
  useUid,
  useValidations,
} from '@rygen/rygen-ui/composables';
import { CapacityProviderType } from '@rygen/rygen-ui/model';

import { useCorsairPermissions } from '@/composables';

import RygenButton from '@rygen/rygen-ui/components/button/RygenButton.vue';
import InputWrapper from '@rygen/rygen-ui/components/form/InputWrapper.vue';
import Modal from '@rygen/rygen-ui/components/ui/Modal.vue';
import InputText from 'primevue/inputtext';

const isVisible = defineModel('visible', {
  type: Boolean,
  default: () => false,
});

const props = defineProps({
  carrierType: {
    type: String as PropType<CapacityProviderType>,
    default: () => CapacityProviderType.CARRIER,
  },
  emails: {
    type: String,
    default: () => '',
  },
});
const { carrierType, emails } = toRefs(props);
const emit = defineEmits<{
  (e: 'confirm-resend-confirmation-clicked', email: string): void;
}>();

const { t } = useI18n();

const internalEmails = ref({ email: emails.value });
const rules = {
  email: {
    required: required(t('common.email')),
    matches: matchesEmail(),
  },
};
const { uid } = useUid('resend-confirmation-modal');
const { shouldDisableTenderEmail } = useCorsairPermissions();
const { v$, validate } = useValidations(internalEmails, rules, uid);

const isAwaitingEmailSent = ref(false);

const emailDisabled = computed(() => {
  return shouldDisableTenderEmail(carrierType.value);
});

const onConfirm = async () => {
  if (await validate()) {
    isAwaitingEmailSent.value = true;
    emit('confirm-resend-confirmation-clicked', internalEmails.value.email);
    isAwaitingEmailSent.value = false;
    isVisible.value = false;
  }
};

const onCancel = () => {
  if (isAwaitingEmailSent.value) {
    return;
  }

  isVisible.value = false;
};

watch(
  emails,
  (newVal) => {
    internalEmails.value.email = newVal;
  },
  { immediate: true },
);
</script>
